/**
 * SuggestResourceForm — page-level "Suggest Resource" form.
 *
 * UX-13 S5: TypeScript (.tsx). The pre-UX-13 §m Q-S5 note deferred @ts-check
 * on callback-density-vs-authoring-cost grounds; the S5 directive supersedes
 * it. The form is fully typed now, reusing the pickers' exported ContactEntry /
 * AffiliationEntry and the OrganizationSubmitPayload / IndividualSubmitPayload
 * wire types (no parallel copies). Payload->entry drafts are asserted at the
 * deserialize boundary (approve-mode prefill reads unknown payload JSON).
 *
 * Architecture:
 *   - BottomSheet primitive wrap (matches 6.F.4 SuggestEditForm aesthetic
 *     + 6.G.2 FilterBar/SearchBar shape discipline).
 *   - Resource Type picker (Organization | Individual) at the top —
 *     state-machine pivot for field rendering.
 *   - Submit POSTs to /v1/suggested-resources via suggestedResourcesApi;
 *     onSuccess invalidates ['admin-review', 'badge-count'] +
 *     ['suggested-resources'] + ['providers'] (last in case approve
 *     happens later in the same session).
 *   - Lookups (subcategories / specialties / service-areas) loaded via
 *     TanStack Query against lookupsApi; staleTime=10min (mirror of
 *     existing picker pattern).
 *
 * Phase 1.7.4 — multi-add affordance on both pickers + inline-create-org
 * on individual-mode + "Contact" label in the contact-person field:
 *   - Org-mode "Contact person(s)" uses IndividualPicker with the new
 *     multi-add API (entries: ContactEntry[]). Wire shape:
 *     payload.contacts: Array<{ existing_id?: string; inline?: IndividualPayload }>.
 *     Server back-compat reads legacy singular fields when contacts[]
 *     is absent.
 *   - Individual-mode "Organization(s)" uses AffiliatedOrgPicker with
 *     the same multi-add shape — replaces the prior search-only
 *     AffiliationPicker that lacked inline-create. Wire shape:
 *     payload.affiliations: Array<{ provider_organization_id?: string;
 *     inline?: OrganizationPayload; role?; title?; is_primary? }>.
 *   - IndividualPicker button label reads "+ Add new contact" (was
 *     "+ Add new individual") — the contextual semantic in this form.
 *
 * UX sensibility (per 6.F.4 §3.7 warm-collaborator voice):
 *   - "Submit for Review" CTA (not "Submit" — names the destination).
 *   - Document-attachment hint at footer per locked decision #5. The
 *     contact mailbox comes from tenants.config.features
 *     .directory_contact_email (useTenantConfig); the hint hides when
 *     the tenant has none configured. No hardcoded fallback address
 *     (CLAUDE.md commercial-product discipline).
 *   - "Submitting as {user.display_name}" small line above the CTA.
 *   - Error messages surface as a top-of-form banner.
 */

import { useEffect, useMemo, useState } from 'react';
import { OverlayPortal } from '@/components/primitives/OverlayPortal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Users } from 'lucide-react';
import { BottomSheet } from '@/components/primitives/BottomSheet';
import { Button } from '@/components/primitives/Button';
import { TextArea, TextInput } from '@/components/primitives/TextInput';
import { nameCase as toNameCase } from '@/lib/valueCase';
import {
  FLD,
  FLD_TEXTAREA,
  FLD_LABEL,
  FLD_REQ,
  FLD_HELP,
  MODAL_FOOT,
  ModalCloseButton,
} from '@/components/primitives/modalFormStyles';
import { CATEGORY_SLUG_TO_DISPLAY } from '@/lib/uiCategoryLabels';
import {
  PAYMENT_TYPES,
  MEDICAID_VALUES,
  GENDER_RESTRICTION_VALUES,
  DELIVERY_MODEL_LOCATIONS,
  HOME_CARE_TYPE_LABELS,
  RESPITE_MODEL_VALUE,
  RESPITE_DISPLAY_LABEL,
} from '@/lib/facetTaxonomy';
import { formatCountyDisplay } from '@/lib/serviceAreas';
import { lookupsApi } from '@/lib/lookups';
import { suggestedResourcesApi } from '@/lib/suggestedResources';
import { ApiError } from '@/lib/api';
import { contactSentence, type SupportContact } from '@/lib/errorCopy';
import { suggestionRefusalText } from '@/lib/suggestionRefusals';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useUnifiedSearch } from '@/hooks/useUnifiedSearch';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { directoryContactEmail } from '@/lib/tenantConfig';
import { IndividualPicker, type ContactEntry } from '@/components/peace-hub-port/IndividualPicker';
import { AffiliatedOrgPicker, type AffiliationEntry } from '@/components/peace-hub-port/AffiliatedOrgPicker';
import { MultiSelectCheckbox } from '@/components/MultiSelectCheckbox';
import { emailValidator, phoneValidator } from '@/lib/validators';
import type { OrganizationSubmitPayload, IndividualSubmitPayload } from '@/lib/suggestedResources';
import type { SearchResult, SuggestedResource } from '@/lib/types';
import type { Dispatch, ElementType, FormEvent, ReactNode, SetStateAction } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { SelectField } from '@/components/primitives/SelectField';
import {
  AddressCheckChoices,
  AddressCheckPrompt,
  AddressFields,
  useAddressForm,
  type AddressForm,
  type AddressFormParts,
} from '@/components/clientRecord/AddressesCard';
import { formatPostalParts, postalPartsOf } from '@/lib/address';

const LOOKUP_STALE_MS = 10 * 60 * 1000;

type TargetType = 'organization' | 'individual';
type FormMode = 'submit' | 'approve';

/** A lookup row (subcategory / specialty / county) as read by the field panels. */
interface LookupRow {
  slug: string;
  display_name: string;
}
/** The subset of a TanStack query the field panels actually read. */
type LookupQuery = Pick<UseQueryResult<{ rows: LookupRow[] }>, 'data' | 'isLoading'>;

interface SuggestResourceFormProps {
  isOpen: boolean;
  onClose: () => void;
  tenantSlug: string;
  submitterDisplayName: string | null;
  mode?: FormMode;
  existingProposal?: SuggestedResource | null;
  onApproveWithEdits?: (finalPayload: object, finalTargetType: string | null) => Promise<void>;
  seedTargetType?: TargetType;
  seedAffiliationEntries?: AffiliationEntry[];
  /** Submit-mode: the name a Directory picker's typed text pre-fills,
   *  as the organization's name and the person's full name both, so
   *  switching the kind keeps it. */
  seedName?: string;
  /** Submit-mode: the suggestion is in (a picker's request). */
  onSubmitted?: () => void;
  /** Submit-mode, for a seat that can edit the Directory: saving adds
   *  the listing in one step (POST /suggested-resources/direct), then
   *  hands it over as a search result, the way a search pick reads. */
  onAdded?: (row: SearchResult) => void;
}

/*
 * Submit-mode callers may pre-seed the form via `seedTargetType` +
 * `seedAffiliationEntries` (team-feedback hotfix, 2026-06-22) — e.g. the org
 * detail page's "+ Add a person" affordance opens the form in individual-mode
 * with the current org pre-linked as an affiliation. Seeds apply on mount, so
 * pre-seeding callers should mount this conditionally (only while open) for a
 * fresh form per open. Defaults preserve the existing (org-mode, no
 * affiliation) behavior for callers that omit them. Ignored in approve-mode
 * (existingProposal wins).
 */
export function SuggestResourceForm({
  isOpen,
  onClose,
  tenantSlug,
  submitterDisplayName,
  mode = 'submit',
  existingProposal = null,
  onApproveWithEdits,
  seedTargetType,
  seedAffiliationEntries,
  seedName,
  onSubmitted,
  onAdded,
}: SuggestResourceFormProps) {
  const seededName = mode !== 'approve' && seedName !== undefined ? seedName : '';
  const adding = mode !== 'approve' && onAdded !== undefined;
  const queryClient = useQueryClient();
  // Phase 1.8 — initial target_type derives from existingProposal in
  // approve-mode (the proposal's submitted type); else a submit-mode seed
  // (hotfix 2026-06-22); else 'organization'.
  const initialTargetType = existingProposal?.target_type ?? seedTargetType ?? 'organization';
  const [targetType, setTargetType] = useState<TargetType>(initialTargetType);
  const [formError, setFormError] = useState<string | null>(null);
  const contact = useSupportContact();
  const [successToast, setSuccessToast] = useState<string | null>(null);
  // Phase 1.8 — type-switch warning state.
  const [pendingTypeSwitch, setPendingTypeSwitch] = useState<TargetType | null>(null);
  const [originalTargetType, setOriginalTargetType] = useState<TargetType>(initialTargetType);

  // Org payload state.
  const [orgName, setOrgName] = useState(seededName);
  const [categorySlug, setCategorySlug] = useState('');
  const [subcatSlugs, setSubcatSlugs] = useState<string[]>([]);
  const [specSlugs, setSpecSlugs] = useState<string[]>([]);
  const [countySlugs, setCountySlugs] = useState<string[]>([]);
  const [phone, setPhone] = useState('');
  const [phone2, setPhone2] = useState('');
  const [fax, setFax] = useState('');
  const [website, setWebsite] = useState('');
  // Phase 1.12 — org email field (data-steward feedback 2026-05-15). Mirrors
  // the indEmail state hook + lifecycle pattern at line 140 below.
  const [orgEmail, setOrgEmail] = useState('');
  const [medicaid, setMedicaid] = useState('');
  const [dementiaUnit, setDementiaUnit] = useState(false);
  // F2 suggest-fields — facet selections (ORG-only; the highlight +
  // service-model assignment tables FK provider_organizations). Values are the
  // shared facet taxonomy display_names (resolve to catalog rows at approve).
  const [genderRestrictions, setGenderRestrictions] = useState('');
  const [paymentTypes, setPaymentTypes] = useState<string[]>([]);
  const [deliveryLocations, setDeliveryLocations] = useState<string[]>([]);
  const [homeCareTypes, setHomeCareTypes] = useState<string[]>([]);
  const [offersRespite, setOffersRespite] = useState(false);
  const [notes, setNotes] = useState('');
  // Phase 1.7.4 — multi-add contact entries. Each entry is either:
  //   { kind: 'existing', id: string }
  //   { kind: 'inline',   draft: IndividualPayload }
  // The button persists between adds; payload serializes to contacts[].
  const [contactEntries, setContactEntries] = useState<ContactEntry[]>([]);

  // Individual payload state.
  const [indFullName, setIndFullName] = useState(seededName);
  const [indTitle, setIndTitle] = useState('');
  const [indEmail, setIndEmail] = useState('');
  const [indBusinessPhone, setIndBusinessPhone] = useState('');
  const [indCellPhone, setIndCellPhone] = useState('');
  const [indFax, setIndFax] = useState('');
  const [indReferralPartner, setIndReferralPartner] = useState(false);
  const [indSpecDisplayNames, setIndSpecDisplayNames] = useState(
    (([]) as string[]),
  );
  const [indNotes, setIndNotes] = useState('');
  // Phase 1.7.4 — multi-add affiliation entries (mirror of contactEntries).
  // Each entry is either:
  //   { kind: 'existing', provider_organization_id: string }
  //   { kind: 'inline',   draft: OrganizationPayload }
  // The button persists between adds; payload serializes to affiliations[].
  const [indAffiliationEntries, setIndAffiliationEntries] = useState<AffiliationEntry[]>(
    // Submit-mode seed (hotfix 2026-06-22). Approve-mode re-seeds from the
    // proposal payload in the isOpen effect below.
    mode !== 'approve' && seedAffiliationEntries ? seedAffiliationEntries : [],
  );
  const [orgSearchTerm, setOrgSearchTerm] = useState('');

  // Reset state when the BottomSheet opens.
  useEffect(() => {
    if (isOpen) {
      setFormError(null);
      setSuccessToast(null);
      // Phase 1.8 — in approve-mode, pre-populate form state from
      // existingProposal.payload. Pre-population runs ONLY on open
      // (not on every re-render) so steward's mid-edit changes aren't
      // clobbered by re-render.
      if (mode === 'approve' && existingProposal) {
        const p = ((existingProposal.payload) as Record<string, unknown>);
        if (existingProposal.target_type === 'organization') {
          setOrgName(((p.name ?? '') as string));
          setCategorySlug(((p.category_slug ?? '') as string));
          setSubcatSlugs(Array.isArray(p.subcategories) ? ((p.subcategories) as string[]) : []);
          setSpecSlugs(Array.isArray(p.specialties) ? ((p.specialties) as string[]) : []);
          setCountySlugs(Array.isArray(p.counties) ? ((p.counties) as string[]) : []);
          setPhone(((p.phone ?? '') as string));
          setPhone2(((p.phone_2 ?? '') as string));
          setFax(((p.fax ?? '') as string));
          orgAddress.reset(postalPartsOf(p.address));
          setWebsite(((p.website ?? '') as string));
          // Phase 1.12 — prefill orgEmail in approve-edit mode.
          setOrgEmail(((p.email ?? '') as string));
          setMedicaid(((p.medicaid ?? '') as string));
          setDementiaUnit(Boolean(p.dementia_unit));
          // F2 suggest-fields — facet prefill. service_models splits back into
          // its three UI controls by membership in the shared taxonomy.
          setGenderRestrictions(((p.gender_restrictions ?? '') as string));
          setPaymentTypes(Array.isArray(p.payment_types) ? ((p.payment_types) as string[]) : []);
          {
            const models = Array.isArray(p.service_models) ? ((p.service_models) as string[]) : [];
            setDeliveryLocations(models.filter((m) => (DELIVERY_MODEL_LOCATIONS as readonly string[]).includes(m)));
            setHomeCareTypes(models.filter((m) => (HOME_CARE_TYPE_LABELS as readonly string[]).includes(m)));
            setOffersRespite(models.includes(RESPITE_MODEL_VALUE));
          }
          setNotes(((p.notes ?? '') as string));
          setContactEntries(buildContactEntriesFromPayload(p));
        } else {
          setIndFullName(((p.full_name ?? '') as string));
          setIndTitle(((p.title ?? '') as string));
          setIndEmail(((p.email ?? '') as string));
          setIndBusinessPhone(((p.business_phone ?? p.phone ?? '') as string));
          setIndCellPhone(((p.cell_phone ?? '') as string));
          setIndFax(((p.fax ?? '') as string));
          orgAddress.reset(postalPartsOf(p.address));
          setIndReferralPartner(Boolean(p.is_referral_partner));
          const specsRaw = p.specialties;
          if (typeof specsRaw === 'string' && specsRaw.length > 0) {
            setIndSpecDisplayNames(specsRaw.split(',').map((s) => s.trim()).filter(Boolean));
          } else {
            setIndSpecDisplayNames([]);
          }
          setIndNotes(((p.notes ?? '') as string));
          setIndAffiliationEntries(buildAffiliationEntriesFromPayload(p));
        }
        setOriginalTargetType(existingProposal.target_type);
      }
    }
    // Intentionally only re-runs on isOpen — populating once per
    // BottomSheet open is the right granularity. Steward edits stay in
    // state until close. The react-hooks/exhaustive-deps lint isn't
    // wired in this project (eslint.config.js doesn't load the plugin),
    // so no disable directive is needed.
  }, [isOpen]);

  // Lookups — subcategories + specialties + service-areas (counties).
  const subcatsQuery = useQuery({
    queryKey: ['lookups', 'subcategories', tenantSlug],
    queryFn: ({ signal }) => lookupsApi.listSubcategories(tenantSlug, { limit: 500 }, signal),
    enabled: isOpen && targetType === 'organization',
    staleTime: LOOKUP_STALE_MS,
  });
  const specsQuery = useQuery({
    queryKey: ['lookups', 'specialties', tenantSlug],
    queryFn: ({ signal }) => lookupsApi.listSpecialties(tenantSlug, { limit: 500 }, signal),
    enabled: isOpen, // either mode renders specialties picker now
    staleTime: LOOKUP_STALE_MS,
  });
  const countiesQuery = useQuery({
    queryKey: ['lookups', 'service-areas', 'county', tenantSlug],
    queryFn: ({ signal }) =>
      lookupsApi.listServiceAreas(tenantSlug, { area_type: 'county', limit: 500 }, signal),
    enabled: isOpen && targetType === 'organization',
    staleTime: LOOKUP_STALE_MS,
  });

  // Tenant config sources the attach-a-document contact mailbox.
  const tenantConfigQuery = useTenantConfig(tenantSlug, { enabled: isOpen });
  const docContactEmail = directoryContactEmail(tenantConfigQuery.data);

  // Affiliation-picker: reuse the directory's resources cache (already
  // loaded once per session via useUnifiedSearch). Filter by name.
  const { resources } = useUnifiedSearch();
  const orgSearchResults = useMemo(() => {
    if (targetType !== 'individual' || !orgSearchTerm.trim()) return [];
    const term = orgSearchTerm.trim().toLowerCase();
    return resources
      .filter((r) => r.name.toLowerCase().includes(term))
      .slice(0, 25);
  }, [targetType, orgSearchTerm, resources]);

  // The address: the shared address form, a Directory address (a town
  // and a state at least), one form for an organization and a person, so
  // switching between them keeps what was typed. A street is checked
  // before the submit; a town and a state alone, or no address, submits
  // at once.
  const orgAddress = useAddressForm({
    existing: null,
    rule: 'directory',
    idPrefix: 'suggest-org',
    done: false,
    saving: false,
    onSave: (parts) => finish(parts),
  });

  /** Send the form, with the organization's address as checked. */
  function finish(address: AddressFormParts | null) {
    const payload = buildPayload(address);
    if (mode === 'approve') {
      approveMutation.mutate({ payload, finalTargetType: targetType !== originalTargetType ? targetType : null });
      return;
    }
    if (adding) {
      // A failed add keeps every typed field (onError shows the sentence).
      addMutation.mutate({ target_type: targetType, payload });
      return;
    }
    // The form clears once the suggestion is in; a failed submit keeps
    // every typed field (onError shows the sentence).
    submitMutation.mutateAsync({ target_type: targetType, payload }).then(
      () => resetForm(),
      () => undefined,
    );
  }

  // Phase 1.8 — build the final payload object from current state.
  function buildPayload(address: AddressFormParts | null) {
    if (targetType === 'organization') {
      // The form submits on Enter, so the builder cases the names too.
      return {
        name: toNameCase(orgName.trim(), 'place'),
        category_slug: categorySlug,
        kind: 'other',
        subcategories: subcatSlugs,
        specialties: specSlugs,
        counties: countySlugs,
        phone: nullableTrim(phone),
        phone_2: nullableTrim(phone2),
        fax: nullableTrim(fax),
        address,
        website: nullableTrim(website),
        // Phase 1.12 — org email on the wire (server-side already accepts
        // it via OrganizationPayload.email at suggestedResources.ts:116).
        email: nullableTrimLower(orgEmail),
        medicaid: medicaid === '' ? null : medicaid,
        dementia_unit: dementiaUnit,
        // F2 suggest-fields — gender restrictions + payment types + the
        // composed service-model set (delivery locations + home-care types +
        // respite). Empty → null so the apply path writes no assignments.
        gender_restrictions: genderRestrictions === '' ? null : genderRestrictions,
        payment_types: paymentTypes.length > 0 ? paymentTypes : null,
        service_models: buildServiceModels(deliveryLocations, homeCareTypes, offersRespite),
        notes: nullableTrim(notes),
        contacts: contactEntriesToWire(contactEntries),
      };
    }
    const specCsv = indSpecDisplayNames.length > 0 ? indSpecDisplayNames.join(', ') : null;
    const title = nullableTrim(indTitle);
    return {
      full_name: toNameCase(indFullName.trim(), 'person'),
      title: title === null ? null : toNameCase(title, 'role'),
      email: nullableTrimLower(indEmail),
      business_phone: nullableTrim(indBusinessPhone),
      cell_phone: nullableTrim(indCellPhone),
      fax: nullableTrim(indFax),
      address,
      is_referral_partner: indReferralPartner,
      specialties: specCsv,
      notes: nullableTrim(indNotes),
      affiliations: affiliationEntriesToWire(indAffiliationEntries),
    };
  }

  // Both writes take the payload and the target type built at the press
  // as their variables (send, below).
  const approveMutation = useMutation({
    mutationFn: async (v: { payload: ReturnType<typeof buildPayload>; finalTargetType: typeof targetType | null }) => {
      if (!onApproveWithEdits) throw new Error('onApproveWithEdits prop missing in approve-mode');
      await onApproveWithEdits(v.payload, v.finalTargetType);
    },
    onSuccess: () => {
      setSuccessToast('Approved with edits. Resource created.');
      setTimeout(() => onClose(), 1200);
    },
    onError: (e) => {
      setFormError(formatSubmitError(e, contact));
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (v: { target_type: typeof targetType; payload: ReturnType<typeof buildPayload> }) =>
      suggestedResourcesApi.submit(tenantSlug, v),
    onSuccess: async () => {
      setSuccessToast("Thank you. Once an admin approves it, you'll see the resource in the Directory.");
      onSubmitted?.();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-review', 'badge-count'] }),
        queryClient.invalidateQueries({ queryKey: ['suggested-resources'] }),
      ]);
      setTimeout(() => {
        onClose();
      }, 1200);
    },
    onError: (e) => {
      setFormError(formatSubmitError(e, contact));
    },
  });

  // A picker's Add: the listing in one step; the picker takes it as a
  // search result.
  const addMutation = useMutation({
    mutationFn: async (v: { target_type: typeof targetType; payload: ReturnType<typeof buildPayload> }) => {
      const added = await suggestedResourcesApi.addDirect(tenantSlug, v);
      if (added.applied_target_id === null) throw new Error('added without a listing');
      return listingAsSearchResult(v.target_type, added.applied_target_id, v.payload);
    },
    onSuccess: async (row) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-review', 'badge-count'] }),
        queryClient.invalidateQueries({ queryKey: ['suggested-resources'] }),
        queryClient.invalidateQueries({ queryKey: ['providers'] }),
      ]);
      onAdded?.(row);
    },
    onError: (e) => {
      setFormError(formatSubmitError(e, contact));
    },
  });

  function resetForm() {
    setOrgName('');
    setCategorySlug('');
    setSubcatSlugs([]);
    setSpecSlugs([]);
    setCountySlugs([]);
    setPhone('');
    setPhone2('');
    setFax('');
    orgAddress.reset(null);
    setWebsite('');
    setOrgEmail('');
    setMedicaid('');
    setDementiaUnit(false);
    setGenderRestrictions('');
    setPaymentTypes([]);
    setDeliveryLocations([]);
    setHomeCareTypes([]);
    setOffersRespite(false);
    setNotes('');
    setContactEntries([]);
    setIndFullName('');
    setIndTitle('');
    setIndEmail('');
    setIndBusinessPhone('');
    setIndCellPhone('');
    setIndFax('');
    setIndReferralPartner(false);
    setIndSpecDisplayNames([]);
    setIndNotes('');
    setIndAffiliationEntries([]);
    setOrgSearchTerm('');
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    if (mode === 'approve') {
      if (targetType === 'organization') {
        if (orgName.trim().length === 0) { setFormError("Enter the organization's name."); return; }
        if (categorySlug === '') { setFormError('Pick a service category.'); return; }
      } else {
        if (indFullName.trim().length === 0) { setFormError('Enter their full name.'); return; }
      }
      sendWithAddress();
      return;
    }
    if (targetType === 'organization') {
      if (orgName.trim().length === 0) {
        setFormError("Enter the organization's name.");
        return;
      }
      if (categorySlug === '') {
        setFormError('Pick a service category.');
        return;
      }
      const phoneErr = phoneValidator(phone);
      if (phoneErr) {
        setFormError(`Phone: ${phoneErr}`);
        return;
      }
      const phone2Err = phoneValidator(phone2);
      if (phone2Err) {
        setFormError(`Secondary phone: ${phone2Err}`);
        return;
      }
      const faxErr = phoneValidator(fax);
      if (faxErr) {
        setFormError(`Fax: ${faxErr}`);
        return;
      }
      // Phase 1.12 — org email validation mirrors the Individual section's
      // emailValidator gate. Empty input passes; non-empty invalid email
      // blocks submit with the same `Email: {err}` format.
      if (orgEmail) {
        const orgEmailErr = emailValidator(orgEmail);
        if (orgEmailErr) {
          setFormError(`Email: ${orgEmailErr}`);
          return;
        }
      }
    } else {
      if (indFullName.trim().length === 0) {
        setFormError('Enter their full name.');
        return;
      }
      const emailErr = emailValidator(indEmail);
      if (emailErr) {
        setFormError(`Email: ${emailErr}`);
        return;
      }
      const businessPhoneErr = phoneValidator(indBusinessPhone);
      if (businessPhoneErr) {
        setFormError(`Business phone: ${businessPhoneErr}`);
        return;
      }
      const cellPhoneErr = phoneValidator(indCellPhone);
      if (cellPhoneErr) {
        setFormError(`Cell phone: ${cellPhoneErr}`);
        return;
      }
      const indFaxErr = phoneValidator(indFax);
      if (indFaxErr) {
        setFormError(`Fax: ${indFaxErr}`);
        return;
      }
    }
    sendWithAddress();
  }

  /** An address goes through the address form (its gaps, then the check
   *  for a street); a form with none sends at once. */
  function sendWithAddress() {
    if (!orgAddress.isEmpty()) {
      orgAddress.pressSave();
      return;
    }
    finish(null);
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={adding ? 'Add to the Directory' : 'Suggest a resource'}
      {...(adding ? {} : { subtitle: 'Add a place or person you trust. An admin reviews it before it goes live.' })}
      headerRight={<ModalCloseButton onClick={onClose} />}
      centered
    >
      <form onSubmit={handleSubmit}>
        <div className="px-[26px] py-[22px]">
        {successToast && (
          <div role="status" className="mb-3 p-3 rounded-xl bg-sage-50 border border-sage-200 text-sm text-sage-700">
            ✓ {successToast}
          </div>
        )}
        {formError && (
          <div
            role="alert"
            className="mb-3 p-3 rounded-xl bg-warm-50 border border-warm-300 text-sm text-ink"
            data-testid="suggest-resource-error"
          >
            {formError}
          </div>
        )}

        {/* Resource Type picker. */}
        <fieldset className="mb-5">
          <legend className={FLD_LABEL}>Resource type</legend>
          {/* The two cards sit side by side where both fit, and stack in a
              narrow sheet, so neither pushes the sheet sideways. */}
          <div className="flex flex-wrap gap-2">
            <RadioCard
              checked={targetType === 'organization'}
              onClick={() => {
                if (targetType === 'organization') return;
                if (mode === 'approve') {
                  setPendingTypeSwitch('organization');
                } else {
                  setTargetType('organization');
                }
              }}
              Icon={Building2}
              label="Organization"
            />
            <RadioCard
              checked={targetType === 'individual'}
              onClick={() => {
                if (targetType === 'individual') return;
                if (mode === 'approve') {
                  setPendingTypeSwitch('individual');
                } else {
                  setTargetType('individual');
                }
              }}
              Icon={Users}
              label="Individual / Contact"
            />
          </div>
        </fieldset>

        {pendingTypeSwitch && (
          <TypeSwitchWarningModal
            from={targetType}
            to={pendingTypeSwitch}
            droppedFields={getDroppedFields(
              targetType,
              pendingTypeSwitch,
              { orgName, categorySlug, subcatSlugs, specSlugs, countySlugs, phone2, fax, website, orgEmail, medicaid, dementiaUnit, genderRestrictions, paymentTypes, deliveryLocations, homeCareTypes, offersRespite, contactEntries },
              { indTitle, indEmail, indCellPhone, indFax, indReferralPartner, indSpecDisplayNames, indAffiliationEntries }
            )}
            onCancel={() => setPendingTypeSwitch(null)}
            onConfirm={() => {
              if (targetType === 'organization' && pendingTypeSwitch === 'individual') {
                setIndFullName(orgName);
                setIndBusinessPhone(phone);
                setIndNotes(notes);
                setIndTitle('');
                setIndEmail('');
                setIndCellPhone('');
    setIndFax('');
                setIndReferralPartner(false);
                setIndSpecDisplayNames([]);
                setIndAffiliationEntries([]);
              } else if (targetType === 'individual' && pendingTypeSwitch === 'organization') {
                setOrgName(indFullName);
                setPhone(indBusinessPhone);
                setNotes(indNotes);
                setCategorySlug('');
                setSubcatSlugs([]);
                setSpecSlugs([]);
                setCountySlugs([]);
                setPhone2('');
                setFax('');
                setWebsite('');
                setOrgEmail('');
                setMedicaid('');
                setDementiaUnit(false);
                setGenderRestrictions('');
                setPaymentTypes([]);
                setDeliveryLocations([]);
                setHomeCareTypes([]);
                setOffersRespite(false);
                setContactEntries([]);
              }
              setTargetType(pendingTypeSwitch);
              setPendingTypeSwitch(null);
            }}
          />
        )}

        {targetType === 'organization' ? (
          <OrganizationFields
            orgName={orgName} setOrgName={setOrgName}
            categorySlug={categorySlug} setCategorySlug={setCategorySlug}
            subcatSlugs={subcatSlugs} setSubcatSlugs={setSubcatSlugs}
            specSlugs={specSlugs} setSpecSlugs={setSpecSlugs}
            countySlugs={countySlugs} setCountySlugs={setCountySlugs}
            phone={phone} setPhone={setPhone}
            phone2={phone2} setPhone2={setPhone2}
            fax={fax} setFax={setFax}
            addressForm={orgAddress}
            website={website} setWebsite={setWebsite}
            orgEmail={orgEmail} setOrgEmail={setOrgEmail}
            medicaid={medicaid} setMedicaid={setMedicaid}
            dementiaUnit={dementiaUnit} setDementiaUnit={setDementiaUnit}
            genderRestrictions={genderRestrictions} setGenderRestrictions={setGenderRestrictions}
            paymentTypes={paymentTypes} setPaymentTypes={setPaymentTypes}
            deliveryLocations={deliveryLocations} setDeliveryLocations={setDeliveryLocations}
            homeCareTypes={homeCareTypes} setHomeCareTypes={setHomeCareTypes}
            offersRespite={offersRespite} setOffersRespite={setOffersRespite}
            notes={notes} setNotes={setNotes}
            contactEntries={contactEntries}
            setContactEntries={setContactEntries}
            subcatsQuery={subcatsQuery}
            specsQuery={specsQuery}
            countiesQuery={countiesQuery}
            tenantSlug={tenantSlug}
          />
        ) : (
          <IndividualFields
            indFullName={indFullName} setIndFullName={setIndFullName}
            indTitle={indTitle} setIndTitle={setIndTitle}
            indEmail={indEmail} setIndEmail={setIndEmail}
            indBusinessPhone={indBusinessPhone} setIndBusinessPhone={setIndBusinessPhone}
            indCellPhone={indCellPhone} setIndCellPhone={setIndCellPhone}
            indFax={indFax} setIndFax={setIndFax}
            addressForm={orgAddress}
            indReferralPartner={indReferralPartner} setIndReferralPartner={setIndReferralPartner}
            indSpecDisplayNames={indSpecDisplayNames} setIndSpecDisplayNames={setIndSpecDisplayNames}
            indNotes={indNotes} setIndNotes={setIndNotes}
            indAffiliationEntries={indAffiliationEntries}
            setIndAffiliationEntries={setIndAffiliationEntries}
            orgSearchTerm={orgSearchTerm}
            setOrgSearchTerm={setOrgSearchTerm}
            orgSearchResults={orgSearchResults}
            resources={resources}
            specsQuery={specsQuery}
            tenantSlug={tenantSlug}
          />
        )}

          {/* Submitting-as + attach-document note stay in the body, above
              the footer action bar. */}
          <div className="mt-2 space-y-2">
            {submitterDisplayName && (
              <p className="text-xs text-ink-soft">
                Submitting as{' '}
                <span className="font-semibold text-sage-800">{submitterDisplayName}</span>
              </p>
            )}
            {docContactEmail && (
              <p className="text-xs text-ink-soft leading-[1.45]">
                If you would like to attach a document to this listing, please email it to{' '}
                <a
                  href={`mailto:${docContactEmail}`}
                  className="text-sage-400 underline hover:text-sage-800"
                >
                  {docContactEmail}
                </a>
                .
              </p>
            )}
          </div>
        </div>

        <div className={MODAL_FOOT}>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitMutation.isPending || addMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitMutation.isPending || approveMutation.isPending || addMutation.isPending}
          >
            {mode === 'approve'
              ? (approveMutation.isPending ? 'Approving…' : 'Approve & create with edits')
              : adding
                ? (addMutation.isPending ? 'Adding…' : 'Add to the Directory')
                : (submitMutation.isPending ? 'Submitting…' : 'Submit for review')}
          </Button>
        </div>
      </form>
    </BottomSheet>
  );
}

/**
 * Prototype KindBtn (modals.jsx) — resource-type picker tile: chip + icon +
 * label, forest border/tint when selected.
 *
 * UX-13 S5: stays a native <button> (this file is consciously allowlisted for
 * it). A radio-style segmented toggle is a native affordance, not a
 * Button-class action — the same call #387 made for the pickers' listbox
 * option rows. The genuine actions (footer Cancel/Submit, the type-switch
 * modal) compose <Button>; RadioCard is the one native affordance that keeps
 * SuggestResourceForm on the raw-button allowlist.
 *
 * @param {{ checked: boolean, onClick: () => void, Icon: import('react').ElementType, label: string }} props
 */
interface RadioCardProps {
  checked: boolean;
  onClick: () => void;
  Icon: ElementType;
  label: string;
}
function RadioCard({ checked, onClick, Icon, label }: RadioCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-[1_1_10rem] min-w-0 flex items-center gap-[11px] px-4 py-[15px] rounded-[14px] border-[1.5px] transition-all duration-[var(--dur-base)] ${
        checked
          ? 'border-sage-800 bg-sage-800/[6%]'
          : 'border-sage-400/20 bg-paper hover:border-sage-400/40'
      }`}
    >
      <span
        className={`w-[38px] h-[38px] shrink-0 rounded-[11px] inline-flex items-center justify-center ${
          checked ? 'bg-sage-800 text-cream' : 'bg-sand text-sage-400'
        }`}
      >
        <Icon size={19} aria-hidden="true" />
      </span>
      <span className="min-w-0 text-left text-[15px] font-semibold text-sage-800">{label}</span>
    </button>
  );
}

type SetStr = Dispatch<SetStateAction<string>>;
type SetStrArr = Dispatch<SetStateAction<string[]>>;
type SetBool = Dispatch<SetStateAction<boolean>>;

interface OrgDropState {
  orgName: string; categorySlug: string; subcatSlugs: string[]; specSlugs: string[];
  countySlugs: string[]; phone2: string; fax: string; website: string; orgEmail: string;
  medicaid: string; dementiaUnit: boolean; genderRestrictions: string; paymentTypes: string[];
  deliveryLocations: string[]; homeCareTypes: string[]; offersRespite: boolean; contactEntries: ContactEntry[];
}
interface IndDropState {
  indTitle: string; indEmail: string; indCellPhone: string; indFax: string; indReferralPartner: boolean;
  indSpecDisplayNames: string[]; indAffiliationEntries: AffiliationEntry[];
}

interface OrganizationFieldsProps {
  orgName: string; setOrgName: SetStr;
  categorySlug: string; setCategorySlug: SetStr;
  subcatSlugs: string[]; setSubcatSlugs: SetStrArr;
  specSlugs: string[]; setSpecSlugs: SetStrArr;
  countySlugs: string[]; setCountySlugs: SetStrArr;
  phone: string; setPhone: SetStr;
  phone2: string; setPhone2: SetStr;
  fax: string; setFax: SetStr;
  addressForm: AddressForm;
  website: string; setWebsite: SetStr;
  orgEmail: string; setOrgEmail: SetStr;
  medicaid: string; setMedicaid: SetStr;
  dementiaUnit: boolean; setDementiaUnit: SetBool;
  genderRestrictions: string; setGenderRestrictions: SetStr;
  paymentTypes: string[]; setPaymentTypes: SetStrArr;
  deliveryLocations: string[]; setDeliveryLocations: SetStrArr;
  homeCareTypes: string[]; setHomeCareTypes: SetStrArr;
  offersRespite: boolean; setOffersRespite: SetBool;
  notes: string; setNotes: SetStr;
  contactEntries: ContactEntry[]; setContactEntries: Dispatch<SetStateAction<ContactEntry[]>>;
  subcatsQuery: LookupQuery;
  specsQuery: LookupQuery;
  countiesQuery: LookupQuery;
  tenantSlug: string;
}
/** Org fields panel. */
function OrganizationFields(props: OrganizationFieldsProps) {
  const subcatRows = props.subcatsQuery.data?.rows ?? [];
  const specRows = props.specsQuery.data?.rows ?? [];
  const countyRows = props.countiesQuery.data?.rows ?? [];

  return (
    <>
      <FormField label="Organization name" required>
        <TextInput
          type="text"
          nameCase="place"
          value={props.orgName}
          onChange={(e) => props.setOrgName(e.target.value)}
          aria-required="true"
          className={inputCls()}
        />
      </FormField>

      <FormField label="Service category" required>
        <SelectField size="field"
          value={props.categorySlug}
          onChange={(e) => props.setCategorySlug(e.target.value)}
          aria-required="true"
          className="w-full"
        >
          <option value="">Select a category…</option>
          {Object.entries(CATEGORY_SLUG_TO_DISPLAY).map(([slug, display]) => (
            <option key={slug} value={slug}>
              {display}
            </option>
          ))}
        </SelectField>
      </FormField>

      <FormField label="Service types">
        <MultiSelectCheckbox
          options={subcatRows}
          getOptionValue={(r) => r.slug}
          getOptionLabel={(r) => r.display_name}
          selectedValues={props.subcatSlugs}
          onChange={props.setSubcatSlugs}
          loading={props.subcatsQuery.isLoading}
          ariaLabel="Service types"
          filterPlaceholder="Filter service types"
        />
      </FormField>

      <FormField label="Specialties">
        <MultiSelectCheckbox
          options={specRows}
          getOptionValue={(r) => r.slug}
          getOptionLabel={(r) => r.display_name}
          selectedValues={props.specSlugs}
          onChange={props.setSpecSlugs}
          loading={props.specsQuery.isLoading}
          ariaLabel="Specialties"
          filterPlaceholder="Filter specialties"
        />
      </FormField>

      <FormField label="Phone">
        <input
          type="tel"
          value={props.phone}
          onChange={(e) => props.setPhone(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <FormField label="Secondary phone (optional)">
        <input
          type="tel"
          value={props.phone2}
          onChange={(e) => props.setPhone2(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <FormField label="Fax (optional)">
        <input
          type="tel"
          value={props.fax}
          onChange={(e) => props.setFax(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <AddressFields idPrefix="suggest-org" {...props.addressForm.fields} />
      <AddressCheckPrompt phase={props.addressForm.check.phase} />
      {props.addressForm.prompting && (
        <div className="flex flex-wrap justify-end gap-2">
          <AddressCheckChoices
            phase={props.addressForm.check.phase}
            busy={false}
            onKeep={() => props.addressForm.choose('checked', false)}
            onUse={() => props.addressForm.choose('checked', true)}
            onSaveAsTyped={() => props.addressForm.choose('saved_as_typed', false)}
          />
        </div>
      )}

      <FormField label="Website">
        <input
          type="url"
          value={props.website}
          onChange={(e) => props.setWebsite(e.target.value)}
          placeholder="https://"
          className={inputCls()}
        />
      </FormField>

      {/* Phase 1.12 — org email field. Server-side already wired
          (OrganizationPayload.email at suggestedResources.ts:116);
          validator + apply INSERT also pre-existed (line 134 + 643). */}
      <FormField label="Email">
        <input
          type="email"
          value={props.orgEmail}
          onChange={(e) => props.setOrgEmail(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <FormField label="Counties served">
        <MultiSelectCheckbox
          options={countyRows}
          getOptionValue={(r) => r.slug}
          getOptionLabel={(r) => formatCountyDisplay(r.display_name)}
          selectedValues={props.countySlugs}
          onChange={props.setCountySlugs}
          loading={props.countiesQuery.isLoading}
          ariaLabel="Counties served"
          filterPlaceholder="Filter counties"
        />
      </FormField>

      {/* Phase 1.7.4 — multi-add contact picker. The "+ Add new contact"
          button persists after each add so multiple contacts can be
          stacked. Each entry renders as its own chip (existing) or
          summary card (inline, with Edit details + ×). Wire shape:
          payload.contacts: Array<{ existing_id? | inline? }>. */}
      <FormField label="Contact person (existing or new)">
        {/* IndividualPicker reads tenant from useTenant() context; it has no
            tenantSlug prop (the .jsx passed one that was silently ignored — a
            dead prop the .tsx promotion surfaced). Removed; behavior unchanged. */}
        <IndividualPicker
          entries={props.contactEntries}
          onChange={props.setContactEntries}
        />
      </FormField>

      <FormField label="Accepts Medicaid">
        <SelectField size="field"
          value={props.medicaid}
          onChange={(e) => props.setMedicaid(e.target.value)}
          className="w-full"
        >
          <option value="">Not assessed</option>
          {MEDICAID_VALUES.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </SelectField>
      </FormField>

      <FormField label="Payment types accepted" hint="All that apply (Medicare, Private Pay, VA, etc.).">
        <MultiSelectCheckbox
          options={[...PAYMENT_TYPES]}
          getOptionValue={(v) => v}
          getOptionLabel={(v) => v}
          selectedValues={props.paymentTypes}
          onChange={props.setPaymentTypes}
          ariaLabel="Payment types accepted"
        />
      </FormField>

      <FormField label="Delivery method">
        <MultiSelectCheckbox
          options={[...DELIVERY_MODEL_LOCATIONS]}
          getOptionValue={(v) => v}
          getOptionLabel={(v) => v}
          selectedValues={props.deliveryLocations}
          onChange={props.setDeliveryLocations}
          ariaLabel="Delivery method"
        />
      </FormField>

      <FormField label="Home care type">
        <MultiSelectCheckbox
          options={[...HOME_CARE_TYPE_LABELS]}
          getOptionValue={(v) => v}
          getOptionLabel={(v) => v}
          selectedValues={props.homeCareTypes}
          onChange={props.setHomeCareTypes}
          ariaLabel="Home care type"
        />
      </FormField>

      <FormField label="Gender restrictions">
        <SelectField size="field"
          value={props.genderRestrictions}
          onChange={(e) => props.setGenderRestrictions(e.target.value)}
          className="w-full"
        >
          <option value="">No restriction</option>
          {GENDER_RESTRICTION_VALUES.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </SelectField>
      </FormField>

      <div className="mb-5 flex items-center gap-[11px]">
        <input
          id="srf-dementia-unit"
          type="checkbox"
          checked={props.dementiaUnit}
          onChange={(e) => props.setDementiaUnit(e.target.checked)}
          className="w-[18px] h-[18px] accent-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        />
        <label htmlFor="srf-dementia-unit" className="text-sm text-ink">
          Secure Dementia Unit
        </label>
      </div>

      <div className="mb-5 flex items-center gap-[11px]">
        <input
          id="srf-offers-respite"
          type="checkbox"
          checked={props.offersRespite}
          onChange={(e) => props.setOffersRespite(e.target.checked)}
          className="w-[18px] h-[18px] accent-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        />
        <label htmlFor="srf-offers-respite" className="text-sm text-ink">
          {RESPITE_DISPLAY_LABEL}
        </label>
      </div>

      <FormField label="Notes / why are you adding this?" hint="How do you know this resource? Any tips for other CMs?">
        <TextArea
          value={props.notes}
          onChange={(e) => props.setNotes(e.target.value)}
          rows={3}
          className={FLD_TEXTAREA}
        />
      </FormField>
    </>
  );
}

interface IndividualFieldsProps {
  indFullName: string; setIndFullName: SetStr;
  indTitle: string; setIndTitle: SetStr;
  indEmail: string; setIndEmail: SetStr;
  indBusinessPhone: string; setIndBusinessPhone: SetStr;
  indCellPhone: string; setIndCellPhone: SetStr;
  indFax: string; setIndFax: SetStr;
  addressForm: AddressForm;
  indReferralPartner: boolean; setIndReferralPartner: SetBool;
  indSpecDisplayNames: string[]; setIndSpecDisplayNames: SetStrArr;
  indNotes: string; setIndNotes: SetStr;
  indAffiliationEntries: AffiliationEntry[]; setIndAffiliationEntries: Dispatch<SetStateAction<AffiliationEntry[]>>;
  orgSearchTerm: string; setOrgSearchTerm: SetStr;
  orgSearchResults: Array<{ id: string; name: string }>;
  resources: Array<{ id: string; name: string }>;
  specsQuery: LookupQuery;
  tenantSlug: string;
}
/** Individual fields panel. */
function IndividualFields(props: IndividualFieldsProps) {
  return (
    <>
      <FormField label="Full name" required>
        <TextInput
          type="text"
          nameCase="person"
          value={props.indFullName}
          onChange={(e) => props.setIndFullName(e.target.value)}
          aria-required="true"
          className={inputCls()}
        />
      </FormField>

      <FormField label="Title (e.g., Director of Memory Care)">
        <TextInput
          type="text"
          nameCase="role"
          value={props.indTitle}
          onChange={(e) => props.setIndTitle(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <FormField label="Email">
        <input
          type="email"
          value={props.indEmail}
          onChange={(e) => props.setIndEmail(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <FormField label="Business phone">
        <input
          type="tel"
          value={props.indBusinessPhone}
          onChange={(e) => props.setIndBusinessPhone(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <FormField label="Cell phone">
        <input
          type="tel"
          value={props.indCellPhone}
          onChange={(e) => props.setIndCellPhone(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <FormField label="Fax (optional)">
        <input
          type="tel"
          value={props.indFax}
          onChange={(e) => props.setIndFax(e.target.value)}
          className={inputCls()}
        />
      </FormField>

      <AddressFields idPrefix="suggest-org" {...props.addressForm.fields} />
      <AddressCheckPrompt phase={props.addressForm.check.phase} />
      {props.addressForm.prompting && (
        <div className="flex flex-wrap justify-end gap-2">
          <AddressCheckChoices
            phase={props.addressForm.check.phase}
            busy={false}
            onKeep={() => props.addressForm.choose('checked', false)}
            onUse={() => props.addressForm.choose('checked', true)}
            onSaveAsTyped={() => props.addressForm.choose('saved_as_typed', false)}
          />
        </div>
      )}

      {/* Phase 1.7.4 — multi-add affiliation picker with inline-create-org
          (mirror of org-mode IndividualPicker pattern). Replaces the
          prior search-only AffiliationPicker. */}
      <FormField label="Organization(s)">
        <AffiliatedOrgPicker
          tenantSlug={props.tenantSlug}
          entries={props.indAffiliationEntries}
          onChange={props.setIndAffiliationEntries}
          searchTerm={props.orgSearchTerm}
          onSearchTermChange={props.setOrgSearchTerm}
          searchResults={props.orgSearchResults}
          resources={props.resources}
        />
      </FormField>

      <FormField label="Specialties">
        <MultiSelectCheckbox
          options={props.specsQuery?.data?.rows ?? []}
          getOptionValue={(r) => r.display_name}
          getOptionLabel={(r) => r.display_name}
          selectedValues={props.indSpecDisplayNames}
          onChange={props.setIndSpecDisplayNames}
          loading={props.specsQuery?.isLoading ?? false}
          ariaLabel="Specialties"
          filterPlaceholder="Filter specialties"
        />
      </FormField>

      <div className="mb-5 flex items-center gap-[11px]">
        <input
          id="srf-ind-referral-partner"
          type="checkbox"
          checked={props.indReferralPartner}
          onChange={(e) => props.setIndReferralPartner(e.target.checked)}
          className="w-[18px] h-[18px] accent-sage-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        />
        <label htmlFor="srf-ind-referral-partner" className="text-sm text-ink">
          Referral partner
        </label>
      </div>

      <FormField label="Notes / why are you adding this?">
        <TextArea
          value={props.indNotes}
          onChange={(e) => props.setIndNotes(e.target.value)}
          rows={3}
          className={FLD_TEXTAREA}
        />
      </FormField>
    </>
  );
}

/** Generic labeled form field wrapper. */
interface FormFieldProps {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}
function FormField({ label, hint, required, children }: FormFieldProps) {
  return (
    <div className="mb-5">
      <label className="block">
        <span className={FLD_LABEL}>
          {label}
          {required && <span className={`${FLD_REQ} ml-0.5`}>*</span>}
        </span>
        {children}
      </label>
      {hint && <p className={FLD_HELP}>{hint}</p>}
    </div>
  );
}

// ---- Helpers ----

function inputCls() {
  // Prototype .fld — shared via modalFormStyles for parity + anti-drift.
  return FLD;
}

function nullableTrim(v: string): string | null {
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * F2 suggest-fields — compose the flat service_models payload array from the
 * three UI controls (delivery locations + home-care types + the respite
 * toggle). All are provider_service_models display_names. Returns null when
 * nothing is selected so the apply path writes no assignments.
 *
 * @param {string[]} deliveryLocations
 * @param {string[]} homeCareTypes
 * @param {boolean} offersRespite
 * @returns {string[] | null}
 */
function buildServiceModels(deliveryLocations: string[], homeCareTypes: string[], offersRespite: boolean): string[] | null {
  const out = [
    ...deliveryLocations,
    ...homeCareTypes,
    ...(offersRespite ? [RESPITE_MODEL_VALUE] : []),
  ];
  return out.length > 0 ? out : null;
}

// Phase 1.8.4 — email-specific normalizer for the form's submit path.
// Mirrors api/_shared/repos/suggestedResources.ts:nullableEmail. The DB
// CHECK constraints on provider_individuals.email + provider_organizations.email
// require lowercase; submitting mixed-case stores mixed-case in
// suggested_resources.payload, then approve fails (23514 → 500).
function nullableTrimLower(v: string): string | null {
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed.toLowerCase();
}

/**
 * Serialize contact entries to the wire-side `contacts` array shape.
 * Entries with `kind: 'existing'` → `{ existing_id: <id> }`.
 * Entries with `kind: 'inline'`   → `{ inline: <draft> }`.
 *
 * @param {Array<{kind: 'existing', id: string} | {kind: 'inline', draft: object}>} entries
 */
function contactEntriesToWire(entries: ContactEntry[]) {
  return entries.map((e) => {
    if (e.kind === 'existing') return { existing_id: e.id };
    return { inline: e.draft };
  });
}

/**
 * Serialize affiliation entries to the wire-side `affiliations` array
 * shape. Per-entry XOR between provider_organization_id and inline.
 *
 * @param {Array<{kind: 'existing', provider_organization_id: string} | {kind: 'inline', draft: object}>} entries
 */
function affiliationEntriesToWire(entries: AffiliationEntry[]) {
  return entries.map((e) => {
    if (e.kind === 'existing') {
      return { provider_organization_id: e.provider_organization_id };
    }
    return { inline: e.draft };
  });
}

/**
 * Reconstruct contact entries from an existing payload (approve-mode
 * pre-population). Reads NEW canonical `contacts` array if present;
 * otherwise falls back to LEGACY singular `contact_individual_id` /
 * `contact_individual_inline` for back-compat against pre-1.7.4
 * pending proposals.
 *
 * @param {Record<string, unknown>} p
 */
function buildContactEntriesFromPayload(p: Record<string, unknown>): ContactEntry[] {
  const contactsRaw = p.contacts;
  if (Array.isArray(contactsRaw)) {
    const out: ContactEntry[] = [];
    for (const c of contactsRaw) {
      if (typeof c !== 'object' || c === null) continue;
      const ce = ((c) as Record<string, unknown>);
      if (typeof ce.existing_id === 'string') {
        out.push({ kind: 'existing', id: ce.existing_id });
      } else if (typeof ce.inline === 'object' && ce.inline !== null) {
        out.push({ kind: 'inline', draft: ce.inline as IndividualSubmitPayload });
      }
    }
    return out;
  }
  // Legacy singular fields.
  if (typeof p.contact_individual_id === 'string' && p.contact_individual_id.length > 0) {
    return [{ kind: (('existing') as const), id: p.contact_individual_id }];
  }
  if (typeof p.contact_individual_inline === 'object' && p.contact_individual_inline !== null) {
    return [{ kind: 'inline' as const, draft: p.contact_individual_inline as IndividualSubmitPayload }];
  }
  return [];
}

/**
 * Reconstruct affiliation entries from an existing payload. Each
 * affiliation entry may carry provider_organization_id (existing) OR
 * inline (new org). Legacy entries with only provider_organization_id
 * continue to map cleanly.
 *
 * @param {Record<string, unknown>} p
 */
function buildAffiliationEntriesFromPayload(p: Record<string, unknown>): AffiliationEntry[] {
  const affsRaw = p.affiliations;
  if (!Array.isArray(affsRaw)) return [];
  const out: AffiliationEntry[] = [];
  for (const a of affsRaw) {
    if (typeof a !== 'object' || a === null) continue;
    const ae = ((a) as Record<string, unknown>);
    if (typeof ae.provider_organization_id === 'string') {
      out.push({ kind: 'existing', provider_organization_id: ae.provider_organization_id });
    } else if (typeof ae.inline === 'object' && ae.inline !== null) {
      out.push({ kind: 'inline', draft: ae.inline as OrganizationSubmitPayload });
    }
  }
  return out;
}

// =====================================================================
// Phase 1.8 — Type-switch warning modal (Q2 warn-and-clear)
// =====================================================================

/**
 * Field-loss map per design plan §9. Returns ONLY the fields that
 * currently have non-empty values.
 *
 * @param {string} from
 * @param {string} to
 * @param {object} orgState
 * @param {object} indState
 * @returns {string[]}
 */
function getDroppedFields(
  from: TargetType,
  to: TargetType,
  orgState: OrgDropState,
  indState: IndDropState,
): string[] {
  const out: string[] = [];
  if (from === 'organization' && to === 'individual') {
    if (orgState.categorySlug) out.push('Service category');
    if (orgState.subcatSlugs && orgState.subcatSlugs.length > 0) out.push(`Service types (${orgState.subcatSlugs.length} selected)`);
    if (orgState.specSlugs && orgState.specSlugs.length > 0) out.push(`Specialties (${orgState.specSlugs.length} selected)`);
    if (orgState.countySlugs && orgState.countySlugs.length > 0) out.push(`Counties (${orgState.countySlugs.length} selected)`);
    if (orgState.phone2) out.push('Secondary phone');
    if (orgState.fax) out.push('Fax');
    if (orgState.website) out.push('Website');
    if (orgState.orgEmail) out.push('Email');
    if (orgState.medicaid) out.push('Medicaid acceptance');
    if (orgState.dementiaUnit) out.push('Dementia unit flag');
    if (orgState.genderRestrictions) out.push('Gender restrictions');
    if (Array.isArray(orgState.paymentTypes) && orgState.paymentTypes.length > 0) out.push(`Payment types (${orgState.paymentTypes.length})`);
    if (Array.isArray(orgState.deliveryLocations) && orgState.deliveryLocations.length > 0) out.push(`Delivery method (${orgState.deliveryLocations.length})`);
    if (Array.isArray(orgState.homeCareTypes) && orgState.homeCareTypes.length > 0) out.push(`Home care type (${orgState.homeCareTypes.length})`);
    if (orgState.offersRespite) out.push('Offers respite');
    if (Array.isArray(orgState.contactEntries) && orgState.contactEntries.length > 0) {
      out.push(`Contact person(s) (${orgState.contactEntries.length})`);
    }
  } else if (from === 'individual' && to === 'organization') {
    if (indState.indTitle) out.push('Title');
    if (indState.indEmail) out.push('Email');
    if (indState.indCellPhone) out.push('Cell phone');
    if (indState.indFax) out.push('Fax');
    if (indState.indReferralPartner) out.push('Referral partner flag');
    if (indState.indSpecDisplayNames && indState.indSpecDisplayNames.length > 0) out.push(`Specialties (${indState.indSpecDisplayNames.length})`);
    if (Array.isArray(indState.indAffiliationEntries) && indState.indAffiliationEntries.length > 0) {
      out.push(`Organizations (${indState.indAffiliationEntries.length})`);
    }
  }
  return out;
}

/**
 * @param {{ from: string, to: string, droppedFields: string[], onCancel: () => void, onConfirm: () => void }} props
 */
interface TypeSwitchWarningModalProps {
  from: TargetType;
  to: TargetType;
  droppedFields: string[];
  onCancel: () => void;
  onConfirm: () => void;
}
function TypeSwitchWarningModal({ from, to, droppedFields, onCancel, onConfirm }: TypeSwitchWarningModalProps) {
  const toLabel = to === 'organization' ? 'Organization' : 'Individual';
  return (
    <OverlayPortal>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[210] flex items-start justify-center bg-sage-900/50 px-5 py-[6vh] overflow-y-auto animate-fade-in"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onCancel();
        }}
      >
        <div className="w-full max-w-md bg-paper rounded-[22px] shadow-sh3 overflow-hidden">
          <div className="px-[26px] pt-[22px] pb-[18px] border-b border-sage-800/[8%]">
            <h3 className="font-display text-lg text-sage-800">Switch to {toLabel}?</h3>
          </div>
          <div className="px-[26px] py-[22px]">
            {droppedFields.length > 0 ? (
              <>
                <p className="text-sm text-ink mb-2">
                  Switching to {toLabel} will lose these fields:
                </p>
                <ul className="text-sm text-ink mb-3 list-disc list-inside space-y-0.5">
                  {droppedFields.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </>
            ) : (
              <p className="text-sm text-ink mb-3">
                What you typed stays when you switch.
              </p>
            )}
            <p className="text-xs text-ink-soft leading-[1.45]">
              {from === 'organization' && to === 'individual'
                ? 'Name and phone are preserved (as Full name and Business phone).'
                : from === 'individual' && to === 'organization'
                  ? 'Full name and Business phone are preserved (as Name and Phone).'
                  : ''}
            </p>
          </div>
          <div className={MODAL_FOOT}>
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onConfirm}>
              Continue switching
            </Button>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}

/** A refused submit as one sentence: a 403 keeps its own sentence (the
 *  role cannot suggest); everything else is a suggestion refusal, so a
 *  401 reads as a lapsed session. */
function formatSubmitError(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError && err.status === 403) {
    return contactSentence("Suggesting resources isn't part of your access. Ask {contact_admin} if you need it.", contact);
  }
  return suggestionRefusalText(err, contact);
}

/** A listing just added from the form, as the Directory search returns
 *  it: the name, the phone, the email and the address the form sent. */
function listingAsSearchResult(
  targetType: TargetType,
  id: string,
  payload: Record<string, unknown>,
): SearchResult {
  const text = (key: string): string | null => {
    const v = payload[key];
    return typeof v === 'string' && v.trim() !== '' ? v : null;
  };
  const parts = postalPartsOf(payload.address ?? null);
  return {
    type: targetType,
    id,
    display_name: (targetType === 'organization' ? text('name') : text('full_name')) ?? '',
    parent_provider_id: null,
    phone: targetType === 'organization' ? text('phone') : (text('business_phone') ?? text('cell_phone')),
    email: text('email'),
    town: parts?.city ?? null,
    address: parts === null ? null : formatPostalParts(parts),
  };
}
